import {
    Component,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit
} from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    BatteryDto,
    BatteryService,
    BatterySetService,
    CreateBatterySetCommand,
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationService,
    PersonsOrganisationDto,
    toLocalDate,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import { batterySearch } from '@flyfreely-portal-ui/resource-ui';
import { ModelItem } from 'libs/rpa/src/lib/details/rpa-edit/rpa-edit.component';
import * as moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { combineLatest, Subject } from 'rxjs';
import { map, startWith, takeUntil } from 'rxjs/operators';
import { BatteryDialoguesService } from '../../battery-dialogues.service';
import { compareBatteries } from '../../helpers';

@Component({
    selector: 'battery-set-add-dialogue',
    templateUrl: './battery-set-add-dialogue.component.html'
})
export class BatterySetAddDialogue implements OnInit, OnDestroy {
    @Input() organisationId: number;

    private workTracker = new WorkTracker();
    working = false;

    private ngUnsubscribe$ = new Subject<void>();

    batterySetForm: FormGroup;
    searchGroup: FormGroup;
    batteryTypeItems: ModelItem[];

    organisation: PersonsOrganisationDto;

    error: any = null;

    // Available batteries
    private batteries = new Subject<BatteryDto[]>();
    availableBatteries: BatteryDto[];
    multipleOwnersInOrg = false;

    selectedBatteryType: string;

    constructor(
        private modal: BsModalRef<BatterySetAddDialogue>,
        private logging: FlyFreelyLoggingService,
        private batteryService: BatteryService,
        private batterySetService: BatterySetService,
        private commonDialoguesService: CommonDialoguesService,
        @Inject(forwardRef(() => BatteryDialoguesService))
        private batteryDialoguesService: BatteryDialoguesService,
        private organisationService: OrganisationService
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.batterySetForm = new FormGroup({
            name: new FormControl(undefined, [Validators.required]),
            assemblyDate: new FormControl(undefined, [Validators.required]),
            batteries: new FormArray([], [Validators.required])
        });

        this.searchGroup = new FormGroup({
            search: new FormControl(undefined)
        });
    }

    ngOnInit() {
        combineLatest([
            this.batteries,
            this.batterySetForm.valueChanges,
            this.searchGroup.controls.search.valueChanges.pipe(
                startWith(this.searchGroup.controls.search.value)
            )
        ])
            .pipe(
                map(([batteries, value, search]) => {
                    const selectedIds = value.batteries.map(
                        (b: BatteryDto) => b.id
                    );
                    const batteryTypeId =
                        value.batteries.length > 0
                            ? value.batteries[0].batteryTypeId
                            : null;
                    if (batteryTypeId != null) {
                        this.selectedBatteryType = `${
                            (<BatteryDto>value.batteries[0]).batteryTypeMake &&
                            (<BatteryDto>value.batteries[0]).batteryTypeModel
                                ? (<BatteryDto>value.batteries[0])
                                      .batteryTypeMake
                                : ''
                        }${
                            (<BatteryDto>value.batteries[0]).batteryTypeMake
                                ? ' '
                                : ''
                        }${
                            (<BatteryDto>value.batteries[0]).batteryTypeModel ??
                            'Unknown make and model'
                        }`;
                    } else {
                        this.selectedBatteryType = null;
                    }
                    return batteries
                        .sort(compareBatteries)
                        .filter(
                            b =>
                                moment(b.purchaseDate).isSameOrBefore(
                                    value.assemblyDate
                                ) &&
                                selectedIds.indexOf(b.id) === -1 &&
                                (batteryTypeId === null ||
                                    b.batteryTypeId === batteryTypeId)
                        )
                        .filter(b =>
                            search == null || search === ''
                                ? true
                                : batterySearch(search, b)
                        );
                })
            )
            // This is done because *ngFor | async isn't working as expected
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(batteries => {
                this.availableBatteries = batteries;
            });
        this.refreshBatteries();
        this.refreshOrganisation();
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    refreshOrganisation() {
        this.organisationService
            .findByIdForUser(this.organisationId, this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(org => (this.organisation = org))
            .add(this.workTracker.createTracker());
    }

    refreshBatteries() {
        this.batteryService
            .findBatteries(this.organisationId, 'SERVICEABLE')
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: batteries => {
                    this.multipleOwnersInOrg =
                        batteries.reduce(
                            (acc, bat) =>
                                acc.includes(bat.organisationId)
                                    ? acc
                                    : acc.concat(bat.organisationId),
                            []
                        ).length > 1;
                    this.batteries.next(batteries);
                },
                error: error => this.logging.error(error)
            })
            .add(this.workTracker.createTracker());
    }

    extractId(battery: BatteryDto) {
        return battery.id;
    }

    addBattery(battery: BatteryDto) {
        (<FormArray>this.batterySetForm.controls.batteries).push(
            new FormControl(battery)
        );
    }

    removeBattery(ix: number) {
        (<FormArray>this.batterySetForm.controls.batteries).removeAt(ix);
    }

    get hasSearchValue() {
        return (
            this.searchGroup.controls.search.value &&
            this.searchGroup.controls.search.value.length > 0
        );
    }

    clearSearch() {
        this.searchGroup.controls.search.reset();
    }

    submit() {
        const { name, assemblyDate, batteries } = this.batterySetForm.value;

        const payload: CreateBatterySetCommand = {
            organisationId: this.organisationId,
            name: name,
            assemblyDate: toLocalDate(assemblyDate),
            batteryIds: batteries.map(this.extractId)
        };

        const doneWorking = this.workTracker.createTracker();

        this.batterySetService.createBatterySet(payload).then(
            results => {
                this.logging.success('Battery set created');
                this.batterySetForm.markAsPristine();
                this.modal.hide();
                doneWorking();
            },
            (error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while saving battery set: ${error.message}`
                );
                doneWorking();
            }
        );
    }

    showBatteryDetails(battery: BatteryDto) {
        this.batteryDialoguesService.showBatteryDetailsDialogue(
            battery,
            this.organisation
        );
    }

    confirmCancel() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Confirm Cancel',
                `You have unsaved changes, are you sure you want to cancel?`,
                'Yes',
                () => Promise.resolve()
            )
            .then(() => this.cancelEdit(), DO_NOTHING);
    }

    cancel() {
        if (this.batterySetForm.dirty) {
            this.confirmCancel();
        } else {
            this.cancelEdit();
        }
    }

    cancelEdit() {
        this.modal.hide();
    }
}
